if (!customElements.get('media-gallery')) {
  class MediaGallery extends HTMLElement {
    constructor() {
      super();

      if (Shopify.designMode) {
        setTimeout(() => this.init(), 200);
      } else {
        this.init();
      }
    }

    disconnectedCallback() {
      window.removeEventListener('on:debounced-resize', this.resizeHandler);

      if (this.resizeInitHandler) {
        window.removeEventListener('on:debounced-resize', this.resizeInitHandler);
      }

      if (this.zoomInitHandler) {
        window.removeEventListener('on:debounced-resize', this.zoomInitHandler);
      }
    }

    init() {
      this.section = this.closest('.js-product');
      this.noSelectedVariant = this.hasAttribute('data-no-selected-variant');
      this.mediaGroupingEnabled = this.hasAttribute('data-media-grouping-enabled')
        && this.getMediaGroupData();
      this.stackedScroll = this.dataset.stackedScroll;
      this.stackedUnderline = this.dataset.stackedUnderline === 'true' && !this.mediaGroupingEnabled;
      this.isFeatured = this.dataset.isFeatured === 'true';
      this.viewer = this.querySelector('.media-viewer');
      this.thumbs = this.querySelector('.media-thumbs');
      this.thumbsItems = this.querySelectorAll('.media-thumbs__item');
      this.controls = this.querySelector('.media-ctrl');
      this.prevBtn = this.querySelector('.media-ctrl__btn[name="prev"]');
      this.nextBtn = this.querySelector('.media-ctrl__btn[name="next"]');
      this.counterCurrent = this.querySelector('.media-ctrl__current-item');
      this.counterTotal = this.querySelector('.media-ctrl__total-items');
      this.liveRegion = this.querySelector('.media-gallery__status');
      this.zoomLinks = this.querySelectorAll('.js-zoom-link');
      this.loadingSpinner = this.querySelector('.loading-spinner');
      this.xrButton = this.querySelector('.media-xr-button');
      this.el = document.querySelector('.media-gallery.zoom');
      if( this.el){
        this._updateZoom();
      }

      if (this.hasAttribute('data-zoom-enabled')) {
        this.galleryModal = this.querySelector('.js-media-zoom-template').content.firstElementChild.cloneNode(true);
      }

      if (this.mediaGroupingEnabled && !this.noSelectedVariant) {
        this.setActiveMediaGroup(this.getMediaGroupFromOptionSelectors());
      }

      if (this.dataset.layout === 'stacked' && theme.mediaMatches.md) {
        this.resizeInitHandler = this.resizeInitHandler || this.initGallery.bind(this);
        window.addEventListener('on:debounced-resize', this.resizeInitHandler);
        this.setVisibleItems();
        this.previousMediaItem = this.querySelector('.media-viewer__item.is-current-variant');
        setTimeout(() => this.customSetActiveMedia(this.previousMediaItem, this.stackedScroll === 'always'), 200);
      } else {
        this.initGallery();
      }

      if (this.zoomLinks) {
        if (this.dataset.zoomTrigger === 'hover') {
          this.triggerZoomInit();
        } else {
          this.zoomLinks.forEach((zoomLink) => {
            zoomLink.addEventListener('click', (evt) => {
              this.triggerZoomInit();
              evt.preventDefault();
            });
          });
        }
      }

      this.section.addEventListener('on:variant:change', this.onVariantChange.bind(this));
    }

    triggerZoomInit() {
      this.zoomInitHandler = this.zoomInitHandler || this.initZoom.bind(this);
      this.zoomEventListener = this.zoomEventListener || this.handleZoomMouseMove.bind(this);
      window.addEventListener('on:debounced-resize', this.zoomInitHandler);
      this.initZoom();
      this.zoomLinks.forEach((zoomLink) => {
        zoomLink.addEventListener('click', (evt) => {
          evt.preventDefault();
        });
      });
    }

    /**
     * Handle a change in variant on the page.
     * @param {Event} evt - variant change event dispatched by variant-picker
     */
    onVariantChange(evt) {
      if (this.mediaGroupingEnabled) {
        this.setActiveMediaGroup(this.getMediaGroupFromOptionSelectors());
      }

      if (evt.detail.variant && evt.detail.variant.featured_media) {
        const variantMedia = this.viewer.querySelector(
          `[data-media-id="${evt.detail.variant.featured_media.id}"]`
        );
        this.customSetActiveMedia(variantMedia, true);
      }
    }

    /**
     * Gets the media group from currently selected variant options.
     * @returns {?object}
     */
    getMediaGroupFromOptionSelectors() {
      const optionSelectors = this.section.querySelectorAll('.option-selector');
      if (optionSelectors.length > this.getMediaGroupData().groupOptionIndex) {
        const selector = optionSelectors[this.getMediaGroupData().groupOptionIndex];
        if (selector.dataset.selectorType === 'dropdown') {
          return selector.querySelector('.custom-select__btn').textContent.trim();
        }
        return selector.querySelector('input:checked').value;
      }
      return null;
    }

    /**
     * Gets the variant media associations for a product.
     * @returns {?object}
     */
    getMediaGroupData() {
      if (typeof this.variantMediaData === 'undefined') {
        const dataEl = this.querySelector('.js-data-variant-media');
        this.variantMediaData = dataEl ? JSON.parse(dataEl.textContent) : false;
      }

      return this.variantMediaData;
    }

    /**
     * Gets an object mapping media to groups, and the reverse
     * @returns {?object}
     */
    getMediaGroupMap() {
      if (!this.mediaGroupMap) {
        this.mediaGroupMap = {
          groups: {}
        };

        // set up grouping
        const variantMediaData = this.getMediaGroupData();
        let currentMediaOptionName = false;
        this.viewerItems = this.querySelectorAll('.media-viewer__item');
        this.viewerItems.forEach((item) => {
          for (let i = 0; i < variantMediaData.variantMedia.length; i += 1) {
            if (parseInt(item.dataset.mediaId, 10) === variantMediaData.variantMedia[i].mediaId) {
              if (currentMediaOptionName !== variantMediaData.variantMedia[i].option) {
                currentMediaOptionName = variantMediaData.variantMedia[i].option;
              }
            }
          }
          if (currentMediaOptionName) {
            if (!this.mediaGroupMap.groups[currentMediaOptionName]) {
              this.mediaGroupMap.groups[currentMediaOptionName] = {
                name: currentMediaOptionName,
                items: []
              };
            }
            const groupItem = { main: item };
            if (this.thumbs) {
              groupItem.thumb = this.thumbs.querySelector(
                `[data-media-id="${item.dataset.mediaId}"].media-thumbs__item`
              );
            }
            this.mediaGroupMap.groups[currentMediaOptionName].items.push(groupItem);
          }
        });

        // add helper
        this.mediaGroupMap.groupFromItem = (item) => {
          const groups = Object.keys(this.mediaGroupMap.groups);
          for (let i = 0; i < groups; i += 1) {
            const group = groups[i];
            for (let j = 0; j < this.mediaGroupMap.groups[group].items.length; j += 1) {
              if (this.mediaGroupMap.groups[group].items[j] === item) {
                return this.mediaGroupMap.groups[group];
              }
            }
          }
          return this.mediaGroupMap.groups[Object.keys(this.mediaGroupMap.groups)[0]];
        };
      }

      return this.mediaGroupMap;
    }

    /**
     * Show only images associated to the current variant
     * @param {string} groupName - optional - Group to show (uses this.currentItem if empty)
     */
    setActiveMediaGroup(groupName) {
      const mediaGroupMap = this.getMediaGroupMap();
      const selectedGroup = groupName
        ? mediaGroupMap.groups[groupName]
        : mediaGroupMap.groupFromItem(this.currentItem);

      if (selectedGroup) {
        if (this.currentGroup !== selectedGroup) {
          this.currentGroup = selectedGroup;
          this.viewerItems.forEach((item) => {
            item.style.display = 'none';
            item.classList.remove('media-viewer__item--single');
          });
          this.thumbsItems.forEach((item) => {
            item.style.display = 'none';
          });

          let currentItemIsVisible = false;
          selectedGroup.items.forEach((item) => {
            item.main.style.display = '';
            if (item.thumb) {
              item.thumb.style.display = '';
            }
            if (item.main === this.currentItem) {
              currentItemIsVisible = true;
            }
          });
          this.setVisibleItems();

          // If current item is not in this group, set it as the active item
          if (!currentItemIsVisible) {
            this.customSetActiveMedia(selectedGroup.items[0].main, true);
          }

          // Handle single images on stacked view
          if (selectedGroup.items.length === 1) {
            selectedGroup.items[0].main.classList.add('media-viewer__item--single');
          }
        }
      } else {
        this.viewerItems.forEach((item) => {
          item.style.display = '';
        });
        this.thumbsItems.forEach((item) => {
          item.style.display = '';
        });
      }
    }

    /**
     * Initialises the media gallery slider and associated controls.
     */
    initGallery() {
      this.setVisibleItems();
      if (this.visibleItems.length <= 1) return;

      this.viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;
      this.currentIndex = Math.round(this.viewer.scrollLeft / this.viewerItemOffset);
      this.currentItem = this.visibleItems[this.currentIndex];
      this.addListeners();

      if (this.thumbs && this.currentItem) {
        this.currentThumb = this.thumbs.querySelector(
          `[data-media-id="${this.currentItem.dataset.mediaId}"]`
        );
      }

      if (!this.isFeatured && document.hasFocus()) {
        // Eager load the slider images for smooth UX
        this.viewer.querySelectorAll('.product-image[loading="lazy"]').forEach((img, index) => {
          setTimeout(() => {
            img.loading = 'eager';
          }, 500 * (index + 1));
        });
      }

      const currentItem = this.querySelector('.media-viewer__item.is-current-variant');
      this.customSetActiveMedia(currentItem, true);
    }

    addListeners() {
      this.viewer.addEventListener('scroll', this.handleScroll.bind(this));
      if (this.controls) this.controls.addEventListener('click', this.handleNavClick.bind(this));
      if (this.thumbs) this.thumbs.addEventListener('click', this.handleThumbClick.bind(this));
      this.resizeHandler = this.resizeHandler || this.handleResize.bind(this);
      window.addEventListener('on:debounced-resize', this.resizeHandler);
    }

    /**
     * Initialized the zoom on hover for desktop
     */
    initZoom() {
      this.zoomLinks.forEach((el) => {
        const zoomWidth = Number(el.querySelector('.zoom-image').dataset.originalWidth || 0);
        const imageWidth = el.querySelector('.product-image').getBoundingClientRect().width;
        if (theme.mediaMatches.md && ((zoomWidth - 75) > (imageWidth))) {
          el.addEventListener('mousemove', this.zoomEventListener);
          el.classList.remove('pointer-events-none');
        } else {
          el.removeEventListener('mousemove', this.zoomEventListener);
          el.classList.add('pointer-events-none');
        }
      });
    }

    /**
     * Handles mouse move over a zoomable image
     * @param {?object} evt - Event object.
     */
    handleZoomMouseMove(evt) {
      const hoverElem = evt.currentTarget;
      const zoomImage = hoverElem.querySelector('.js-zoom-image');

      // Download the zoom image if necessary
      if (zoomImage.dataset.src) {
        this.loadingSpinner.classList.remove('loading-spinner--out');

        const img = new Image();
        img.src = zoomImage.dataset.src;
        img.onload = () => {
          zoomImage.src = img.src;
          hoverElem.classList.remove('media--zoom-not-loaded');
          this.loadingSpinner.classList.add('loading-spinner--out');
        };
        zoomImage.removeAttribute('data-src');
      }

      try {
        const offsetX = evt.offsetX ? evt.offsetX : evt.touches[0].pageX;
        const offsetY = evt.offsetY ? evt.offsetY : evt.touches[0].pageY;
        const x = (offsetX / zoomImage.offsetWidth) * 100;
        const y = (offsetY / zoomImage.offsetHeight) * 100;
        zoomImage.style.objectPosition = `${x}% ${y}%`;
      } catch (err) {
        // Fail silently
      }
    }

    /**
     * Handles 'scroll' events on the main media container.
     */
    handleScroll() {
      const newIndex = Math.round(this.viewer.scrollLeft / this.viewerItemOffset);

      if (newIndex !== this.currentIndex) {
        const viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;

        // If scroll wasn't caused by a resize event, update the active media.
        if (viewerItemOffset === this.viewerItemOffset) {
          this.customSetActiveMedia(this.visibleItems[newIndex], false);
        }
      }
    }

    /**
     * Handles 'click' events on the controls container.
     * @param {object} evt - Event object.
     */
    handleNavClick(evt) {
      if (!evt.target.matches('.media-ctrl__btn')) return;

      const itemToShow = evt.target === this.nextBtn
        ? this.visibleItems[this.currentIndex + 1]
        : this.visibleItems[this.currentIndex - 1];

      this.viewer.scrollTo({ left: itemToShow.offsetLeft, behavior: 'smooth' });

      setTimeout(() => {
        this._updateZoom()
      }, 500);
    }

    _updateZoom() {
        if(document.querySelector('.img-zoom-lens')){
          document.querySelector('.img-zoom-lens').remove();
        }
        var allImg = this.el.querySelectorAll('.media-viewer__item img');
        allImg.forEach((_img) => {
          _img.removeAttribute('id');
          
        });
        let img = this.el.querySelector('.media-viewer__item.is-current-variant img');
        if(this.currentItem) {
          img = this.currentItem.querySelector('img');
        }

        img.setAttribute('id','zoom-image');
        img.addEventListener('dblclick',this._bdClick.bind(this));
        img.setAttribute('id','zoom-image');
        this.imageZoom('zoom-image');
     };
   
    imageZoom(img_id) {
      var img, lens, cx, cy;
      img = document.getElementById(img_id);
      lens = document.createElement("DIV");
      lens.setAttribute("class", "img-zoom-lens");
      img.parentElement.insertBefore(lens, img);
      cx = 1.5;
      cy = 1.5;
      lens.style.backgroundImage = "url('" + img.src + "')";
      lens.style.backgroundSize = (img.width * cx) + "px " + (img.height * cy) + "px";
      lens.style.backgroundRepeat = 'no-repeat';
  
          var _gallery = document.querySelector('.media-viewer');
      if(_gallery) {
        _gallery.addEventListener("mousemove", moveLens);
        _gallery.addEventListener("mouseout", hiddenLens);
      }
  
      function hiddenLens() {
        lens.style.opacity = 0;
      }
  
      function moveLens(e) {
        var pos, x, y;
        e.preventDefault();
        pos = getCursorPos(e);
        x = pos.x - (lens.offsetWidth / 2);
        y = pos.y - (lens.offsetHeight / 2);
        if (x > img.width - lens.offsetWidth) {x = img.width - lens.offsetWidth -2;}
  
        if (x < 0) {x = 1;}
        if (y > img.height - lens.offsetHeight) {y = img.height - lens.offsetHeight -2;}
  
        if (y < 0) {y = 1;}
        if(pos.x < 0 || pos.x > img.width || pos.y < 0 || pos.y > img.height) {
          lens.style.opacity = 0;
        }
        if(pos.x > 0 && pos.y > 0 && pos.x < img.width && pos.y < img.height) {
          lens.style.opacity = 1;
        }
        lens.style.left = x + "px";
        lens.style.top = y + "px";
        lens.style.backgroundPosition = "-" + (x * cx) + "px -" + (y * cy) + "px";
      }
      function getCursorPos(e) {
        var a, x = 0, y = 0;
        e = e || window.event;
        a = img.getBoundingClientRect();
        x = e.pageX - a.left;
        y = e.pageY - a.top;
        x = x - window.pageXOffset;
        y = y - window.pageYOffset;
        return {x : x, y : y};
      }
    }

    /**
     * Handles 'click' events on the thumbnails container.
     * @param {object} evt - Event object.
     */
    handleThumbClick(evt) {
      const thumb = evt.target.closest('[data-media-id]');
      if (!thumb) return;

      const itemToShow = this.querySelector(`[data-media-id="${thumb.dataset.mediaId}"]`);
      this.customSetActiveMedia(itemToShow);

      MediaGallery.playActiveMedia(itemToShow);
    }

    /**
     * Handles debounced 'resize' events on the window.
     */
    handleResize() {
      // Reset distance from leading edge of one slide to the next.
      this.viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;

      if (this.thumbs && this.currentThumb) {
        this.checkThumbVisibilty(this.currentThumb);
      }
    }

    /**
     * Stub for variant-picker calls. Listening to change event instead.
     */
    // eslint-disable-next-line class-methods-use-this
    setActiveMedia() {}

    /**
     * Sets the active media item.
     * @param {Element} mediaItem - Media element to set as active.
     * @param {boolean} [scrollToItem=true] - Scroll to the active media item.
     */
    customSetActiveMedia(mediaItem, scrollToItem = true) {
      if (mediaItem === this.currentItem) return;
      window.pauseAllMedia(this);
      this.currentItem = mediaItem;
      this.currentIndex = this.visibleItems.indexOf(this.currentItem);

      if (this.dataset.layout === 'stacked' && theme.mediaMatches.md) {
        // Update the active class and scroll to the active media
        if (this.stackedUnderline) {
          if (this.previousMediaItem) this.previousMediaItem.classList.remove('is-active');
          mediaItem.classList.add('is-active');
          this.previousMediaItem = mediaItem;
        }

        if (this.stackedScroll !== 'never') {
          const y = mediaItem.getBoundingClientRect().top
            + document.documentElement.scrollTop - 150;

          // If the element is far enough away to scroll to it
          if (Math.abs(y - document.documentElement.scrollTop) > 300) {
            window.scrollTo({
              top: y < 100 ? 0 : y,
              behavior: 'smooth'
            });
          }
        }
        return;
      }

      if (scrollToItem) this.viewer.scrollTo({ left: this.currentItem.offsetLeft });
      if (this.thumbs) this.setActiveThumb();

      if (this.controls) {
        if (this.prevBtn) {
          this.prevBtn.disabled = this.currentIndex === 0;
        }

        if (this.nextBtn) {
          this.nextBtn.disabled = this.currentIndex === this.visibleItems.length - 1;
        }

        if (this.counterCurrent) {
          this.counterCurrent.textContent = this.currentIndex + 1;
        }
      }

      this.announceLiveRegion(this.currentItem, this.currentIndex + 1);

      if (this.xrButton && mediaItem.dataset.mediaType === 'model') {
        this.xrButton.dataset.shopifyModel3dId = mediaItem.dataset.mediaId;
      }
    }

    /**
     * Sets the active thumbnail.
     */
    setActiveThumb() {
      this.currentThumb = this.thumbs.querySelector(
        `[data-media-id="${this.currentItem.dataset.mediaId}"]`
      );
      const btn = this.currentThumb.querySelector('button');

      this.thumbs.querySelectorAll('.media-thumbs__btn').forEach((el) => {
        el.classList.remove('is-active');
        el.removeAttribute('aria-current');
      });

      btn.classList.add('is-active');
      btn.setAttribute('aria-current', 'true');
      this.checkThumbVisibilty(this.currentThumb);

      setTimeout(() => {
        this._updateZoom()
      }, 500);
    }

    /**
     * Creates an array of the visible media items.
     */
    setVisibleItems() {
      this.viewerItems = this.querySelectorAll('.media-viewer__item');
      this.visibleItems = Array.from(this.viewerItems).filter((el) => el.clientWidth > 0);
      if (this.counterTotal) {
        this.counterTotal.textContent = this.visibleItems.length;
      }
    }

    /**
     * Ensures a thumbnail is in the visible area of the slider.
     * @param {Element} thumb - Thumb item element.
     */
    checkThumbVisibilty(thumb) {
      const scrollPos = this.thumbs.scrollLeft;
      const lastVisibleThumbOffset = this.thumbs.clientWidth + scrollPos;
      const thumbOffset = thumb.offsetLeft;

      if (thumbOffset + thumb.clientWidth > lastVisibleThumbOffset || thumbOffset < scrollPos) {
        this.thumbs.scrollTo({ left: thumbOffset, behavior: 'smooth' });
      }
    }

    /**
     * Updates the media gallery status.
     * @param {Element} mediaItem - Active media element.
     * @param {number} index - Active media index.
     */
    announceLiveRegion(mediaItem, index) {
      const image = mediaItem.querySelector('.media-viewer img');
      if (!image) return;

      this.liveRegion.setAttribute('aria-hidden', 'false');
      this.liveRegion.innerHTML = theme.strings.imageAvailable.replace('[index]', index);

      setTimeout(() => {
        this.liveRegion.setAttribute('aria-hidden', 'true');
      }, 2000);
    }

    /**
     * Loads the deferred media for the active item.
     * @param {Element} mediaItem - Active media element.
     */
    static playActiveMedia(mediaItem) {
      window.pauseAllMedia();
      const deferredMedia = mediaItem.querySelector('deferred-media');
      if (deferredMedia) deferredMedia.loadContent();
    }

    _bdClick(env){
     const src =  env?.target?.getAttribute('src');
     if(src){
      const _body = document.querySelector('body');
      const _pinchBlock = document.createElement("DIV");
      const _pinchContent = document.createElement("DIV");
      const _pinchContentImg = document.createElement("DIV");
      const _img = document.createElement("IMG");
      const _btn = document.createElement("BUTTON");
      _pinchBlock.classList.add("pinch-zoom");
      _body.appendChild(_pinchBlock);
      _pinchContent.setAttribute("class", "pinch-zoom_content");
      _pinchBlock.appendChild(_pinchContent);
      _pinchContentImg.setAttribute("class", "pinch-zoom_content-img");
      _pinchContent.appendChild(_pinchContentImg);
      _img.setAttribute("src", src);
      _img.setAttribute("class", "zoom-pinch-img");
      _pinchContentImg.appendChild(_img);
      _btn.setAttribute("class", "close-pinch");
      _pinchContent.appendChild(_btn);
      _btn.addEventListener('click', this._remove.bind(this));
      this.pinchElement();
     }
    }

    _remove(){
      const _pinch_zoom = document.querySelector('.pinch-zoom');
      _pinch_zoom.remove();
    }

    pinchElement(){
      var element = document.querySelector('.zoom-pinch-img');
      if(element) {
          var hammertime = new Hammer(element, {});
  
          hammertime.get('pinch').set({enable: true});
          hammertime.get('pan').set({threshold: 0});
  
          var fixHammerjsDeltaIssue = undefined;
          var pinchStart = {x: undefined, y: undefined}
          var lastEvent = undefined;
  
          var _eW = element.clientWidth;
          var _pW = element.parentElement.clientWidth
          var _eH = element.clientHeight;
          var _pH = element.parentElement.clientHeight
  
          var originalSize = {
              width: _eW,
              height: _eH
          }
  
          var current = {
              x: (_pW - _eW) / 2,
              y: (_pH - _eH) / 2,
              z: 1,
              zooming: false,
              width: originalSize.width * 1,
              height: originalSize.height * 1,
          }
  
          var initPos = {
              x: (_pW - _eW) / 2,
              y: 0,
              z: 1,
          }
  
          var last = {
              x: current.x,
              y: current.y,
              z: current.z
          }
          update(true);
  
          function getRelativePosition(element, point, originalSize, scale) {
              var domCoords = getCoords(element);
  
              var elementX = point.x - domCoords.x;
              var elementY = point.y - domCoords.y;
  
              var relativeX = elementX / (originalSize.width * scale / 2) - 1;
              var relativeY = elementY / (originalSize.height * scale / 2) - 1;
              return {x: relativeX, y: relativeY}
          }
  
          function getCoords(elem) { // crossbrowser version
              var box = elem.getBoundingClientRect();
  
              var body = document.body;
              var docEl = document.documentElement;
  
              var scrollTop = window.pageYOffset || docEl.scrollTop || body.scrollTop;
              var scrollLeft = window.pageXOffset || docEl.scrollLeft || body.scrollLeft;
  
              var clientTop = docEl.clientTop || body.clientTop || 0;
              var clientLeft = docEl.clientLeft || body.clientLeft || 0;
  
              var top = box.top + scrollTop - clientTop;
              var left = box.left + scrollLeft - clientLeft;
  
              return {x: Math.round(left), y: Math.round(top)};
          }
  
          function scaleFrom(zoomOrigin, currentScale, newScale) {
              var currentShift = getCoordinateShiftDueToScale(originalSize, currentScale);
              var newShift = getCoordinateShiftDueToScale(originalSize, newScale);
  
              var zoomDistance = newScale - currentScale
  
              var shift = {
                  x: currentShift.x - newShift.x,
                  y: currentShift.y - newShift.y,
              }
  
              var output = {
                  x: zoomOrigin.x * shift.x,
                  y: zoomOrigin.y * shift.y,
                  z: zoomDistance
              }
              return output;
          }
  
          function getCoordinateShiftDueToScale(size, scale) {
              var newWidth = scale * size.width;
              var newHeight = scale * size.height;
              var dx = (newWidth - size.width) / 2;
              var dy = (newHeight - size.height) / 2;
              return {
                  x: dx,
                  y: dy
              };
          }
  
          hammertime.on('doubletap', function (e) {
              var init = false;
              var scaleFactor = 1;
              if (current.zooming === false) {
                  current.zooming = true;
              } else {
                  current.zooming = false;
                  scaleFactor = -scaleFactor;
                  init = true;
              }
  
              element.style.transition = "0.3s";
              setTimeout(function () {
                  element.style.transition = "none";
              }, 300)
  
              var zoomOrigin = getRelativePosition(element, {x: e.center.x, y: e.center.y}, originalSize, current.z);
              var d = scaleFrom(zoomOrigin, current.z, current.z + scaleFactor)
              current.x += d.x;
              current.y += d.y;
              current.z += d.z;
  
              last.x = current.x;
              last.y = current.y;
              last.z = current.z;
  
              update(init);
          })
  
          hammertime.on('pan', function (e) {
              if (lastEvent !== 'pan') {
                  fixHammerjsDeltaIssue = {
                      x: e.deltaX,
                      y: e.deltaY
                  };
              }
              ;
  
              current.x = last.x + e.deltaX - fixHammerjsDeltaIssue.x;
              current.y = last.y + e.deltaY - fixHammerjsDeltaIssue.y;
              lastEvent = 'pan';
              update();
          })
  
          hammertime.on('pinch', function (e) {
              var d = scaleFrom(pinchZoomOrigin, last.z, last.z * e.scale)
              current.x = d.x + last.x + e.deltaX;
              current.y = d.y + last.y + e.deltaY;
              current.z = d.z + last.z;
              lastEvent = 'pinch';
              update();
          })
  
          var pinchZoomOrigin = undefined;
          hammertime.on('pinchstart', function (e) {
              pinchStart.x = e.center.x;
              pinchStart.y = e.center.y;
              pinchZoomOrigin = getRelativePosition(element, {x: pinchStart.x, y: pinchStart.y}, originalSize, current.z);
              lastEvent = 'pinchstart';
          })
  
          hammertime.on('panend', function (e) {
              last.x = current.x;
              last.y = current.y;
              lastEvent = 'panend';
          })
  
          hammertime.on('pinchend', function (e) {
              last.x = current.x;
              last.y = current.y;
              last.z = current.z;
              lastEvent = 'pinchend';
          })
  
          function update(init = false) {
              current.height = originalSize.height * current.z;
              current.width = originalSize.width * current.z;
              if(init) {
                  element.style.transform = "translate3d(0,0,0)";
                  element.style.transform = "translate3d(" + initPos.x + "px, " + initPos.y + "px, 0) scale(" + initPos.z + ")";
  
              } else {
                  element.style.transform = "translate3d(" + current.x + "px, " + current.y + "px, 0) scale(" + current.z + ")";
  
              }
          }
      }
    }
  }

  customElements.define('media-gallery', MediaGallery);
}


/*
this.loupe = this.querySelector('.zoom-loupe');

document.addEventListener('mousemove', function(event) {
  console.log('Mouse X:', event.clientX, 'Mouse Y:', event.clientY);
  this.loupe.style.top = event.clientY;
  this.loupe.style.left = event.clientX;
});

*/